import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * The emojis a seller actually reaches for, in WhatsApp's own groups.
 *
 * A short list on purpose, not the whole Unicode set: no download, no search
 * index to ship, and nothing newer than Emoji 12 — a client on an older phone
 * would get an empty box instead of the face. The keyboard's own picker
 * (Windows + . or Ctrl + Cmd + Space) still types anything else.
 */
const GROUPS = {
  smileys:
    "😀 😃 😄 😁 😆 😅 😂 🤣 😊 😇 🙂 🙃 😉 😌 😍 🥰 😘 😗 😋 😛 😜 🤪 😝 🤑 🤗 🤭 🤫 🤔 🤐 🤨 😐 😑 😶 😏 😒 🙄 😬 😔 😪 🤤 😴 😷 🤒 🤕 🤢 🥵 🥶 😵 🤯 🤠 🥳 😎 🤓 🧐 😕 😟 🙁 😮 😯 😲 😳 🥺 😦 😧 😨 😰 😥 😢 😭 😱 😖 😣 😞 😓 😩 😫 🥱 😤 😡 😠",
  people:
    "👍 👎 👌 ✌️ 🤞 🤟 🤘 🤙 👈 👉 👆 👇 ☝️ ✋ 🤚 🖐️ 👋 👏 🙌 👐 🤲 🤝 🙏 ✍️ 💪 👀 🙋 🙋‍♂️ 🙋‍♀️ 🤷 🤷‍♂️ 🤷‍♀️ 🙇 💁 🙆 🙅 👨‍💼 👩‍💼 👷 👨‍🔧 👩‍🔧",
  symbols:
    "❤️ 🧡 💛 💚 💙 💜 🖤 🤍 🤎 💔 💕 💯 ✅ ☑️ ✔️ ❌ ⚠️ ❗ ❓ ‼️ ⭐ 🌟 ✨ 🔥 💥 🎉 🎊 🎁 🏆 🥇 🔔 📢 💬 🆗 🆕 🔝",
  work: "📄 📃 📑 📊 📈 📉 📋 📁 📂 🗂️ 📎 📌 📍 ✏️ 🖊️ 📝 💼 📅 📆 ⏰ ⏳ 📞 ☎️ 📱 💻 🖥️ 🖨️ 📧 📨 📦 🚚 🚛 🏭 🏗️ 🏢 🔧 🔨 🛠️ ⚙️ 🔩 🧰 🧱 💰 💵 💲 💳 🧾 🏦 🔑 🔒",
  more: "☀️ 🌤️ ⛅ 🌧️ ⛈️ ❄️ 🌈 🌎 🌵 🌹 🌻 🌷 🍀 ☕ 🍺 🍻 🥂 🍕 🌮 🌯 🍔 🍰 🎂 🚗 ✈️ 🚀 ⚽ 🏈 🏁 🇲🇽",
} as const;

type Group = keyof typeof GROUPS;

const ICONS: Record<Group, string> = {
  smileys: "😊",
  people: "👍",
  symbols: "❤️",
  work: "💼",
  more: "🌎",
};

const ORDER = Object.keys(GROUPS) as Group[];

/**
 * Opens above the composer, as WhatsApp's does, and stays open while the
 * agent picks several. Its own button or Escape closes it.
 */
export function EmojiPicker({
  onPick,
  onClose,
}: {
  onPick: (emoji: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [group, setGroup] = useState<Group>("smileys");

  // On the document, not on the panel: after a pick the focus is back in the
  // text box, which is exactly where the agent presses Escape from.
  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", close);
    return () => document.removeEventListener("keydown", close);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-label={t("inbox.emoji")}
      className="absolute bottom-full left-0 z-30 flex h-72 w-full flex-col overflow-hidden border-t border-wa-line bg-white shadow-lg sm:left-3 sm:mb-1 sm:w-[352px] sm:rounded-[12px] sm:border-0"
    >
      <div role="tablist" className="flex border-b border-wa-line">
        {ORDER.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={group === key}
            aria-label={t(`inbox.emojiGroups.${key}`)}
            title={t(`inbox.emojiGroups.${key}`)}
            onClick={() => setGroup(key)}
            className={`flex-1 border-b-2 py-2 text-xl ${
              group === key ? "border-wa-green-dark" : "border-transparent opacity-60"
            }`}
          >
            {ICONS[key]}
          </button>
        ))}
      </div>
      <div
        role="tabpanel"
        aria-label={t(`inbox.emojiGroups.${group}`)}
        className="grid flex-1 grid-cols-8 content-start gap-0.5 overflow-y-auto p-2"
      >
        {GROUPS[group].split(" ").map((emoji) => (
          <button
            key={emoji}
            type="button"
            onClick={() => onPick(emoji)}
            className="flex h-10 items-center justify-center rounded-[6px] text-[26px] leading-none hover:bg-wa-panel"
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );
}
